import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { api, setSession } from "../lib/api";
import { BrandMark, Footer, Spinner } from "../components/Brand";
import { useEffect } from "react";
import { ROLE_LABEL } from "../lib/fr";

export function Forgot() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email });
      setDone(true);
    } catch {
      setDone(true);
    } finally { setBusy(false); }
  }
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--cs-bg)" }}>
      <Toaster position="top-center" richColors />
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[440px] cs-fade">
          <div className="mb-8"><BrandMark size="lg" /></div>
          {!done ? (
            <form onSubmit={submit} className="cs-card p-7 flex flex-col gap-4" data-testid="forgot-password">
              <h2 className="font-semibold text-[20px]" style={{ letterSpacing: "-0.4px" }}>Mot de passe oublié</h2>
              <p className="text-[14px]" style={{ color: "var(--cs-text-muted)" }}>
              Saisissez votre email professionnel — nous vous enverrons un lien de réinitialisation valable 24 h.
              </p>
              <input className="cs-input" type="email" placeholder="medecin@hopital.fr"
                     value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
              <button className="cs-btn-primary" type="submit" disabled={busy} data-testid="forgot-submit">
                {busy ? <Spinner /> : "Envoyer le lien"}
              </button>
              <Link to="/" className="text-center text-[13px] hover:underline" style={{ color: "var(--cs-text-muted)" }}>
                ← Retour à la connexion
              </Link>
            </form>
          ) : (
            <div className="cs-card p-7 text-center" data-testid="forgot-success">
              <div className="text-[17px] font-semibold mb-2">Lien envoyé</div>
              <div className="text-[14px]" style={{ color: "var(--cs-text-muted)" }}>
                Si un compte existe pour <b>{email}</b>, un email a été envoyé.
              </div>
              <Link to="/" className="cs-btn-primary mt-5 inline-flex" data-testid="back-to-login">
                Retour à la connexion
              </Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function Reset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Mot de passe trop court (min 6 caractères)");
    if (pw !== pw2) return toast.error("Les mots de passe ne correspondent pas");
    setBusy(true);
    try {
      await api.post("/auth/reset", { token, password: pw });
      toast.success("Mot de passe réinitialisé");
      setTimeout(() => navigate("/"), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--cs-bg)" }}>
      <Toaster position="top-center" richColors />
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[440px] cs-fade">
          <div className="mb-8"><BrandMark size="lg" /></div>
          <form onSubmit={submit} className="cs-card p-7 flex flex-col gap-4" data-testid="reset-password">
            <h2 className="font-semibold text-[20px]" style={{ letterSpacing: "-0.4px" }}>Nouveau mot de passe</h2>
            <input className="cs-input" type="password" placeholder="Nouveau mot de passe"
                   value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw1" />
            <input className="cs-input" type="password" placeholder="Confirmer le mot de passe"
                   value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2" />
            <button className="cs-btn-primary" type="submit" disabled={busy} data-testid="reset-submit">
              {busy ? <Spinner /> : "Définir le mot de passe"}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Lien invalide"));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Mot de passe trop court (min 6 caractères)");
    if (pw !== pw2) return toast.error("Les mots de passe ne correspondent pas");
    setBusy(true);
    try {
      const { data } = await api.post("/invitations/accept", { token, password: pw });
      setSession(data);
      const dest = data.user.role === "im" ? "/im" : data.user.role === "obgyn" ? "/obgyn" : "/admin";
      navigate(dest, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--cs-bg)" }}>
      <Toaster position="top-center" richColors />
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[440px] cs-fade">
          <div className="mb-8"><BrandMark size="lg" /></div>
          {err ? (
            <div className="cs-card p-7 text-center" data-testid="invite-error">
              <div className="text-[17px] font-semibold mb-2">Lien invalide</div>
              <div className="text-[14px]" style={{ color: "var(--cs-text-muted)" }}>{err}</div>
              <Link to="/" className="cs-btn-primary mt-5 inline-flex">Retour</Link>
            </div>
          ) : !info ? (
            <div className="cs-card p-7 text-center"><Spinner /></div>
          ) : (
            <form onSubmit={submit} className="cs-card p-7 flex flex-col gap-4" data-testid="invitation-accept">
              <div>
                <h2 className="font-semibold text-[20px]" style={{ letterSpacing: "-0.4px" }}>Bienvenue, {info.name}</h2>
                <p className="text-[14px] mt-1" style={{ color: "var(--cs-text-muted)" }}>
                  Vous rejoignez ContracepSur en tant que <b>{ROLE_LABEL[info.role]}</b>.
                </p>
                <p className="text-[13px] mt-2" style={{ color: "var(--cs-text-subtle)" }}>{info.email}</p>
              </div>
              <input className="cs-input" type="password" placeholder="Définir un mot de passe"
                     value={pw} onChange={(e) => setPw(e.target.value)} data-testid="invite-pw1" />
              <input className="cs-input" type="password" placeholder="Confirmer le mot de passe"
                     value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="invite-pw2" />
              <button className="cs-btn-primary" type="submit" disabled={busy} data-testid="invite-submit">
                {busy ? <Spinner /> : "Activer mon compte"}
              </button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
